import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * TickingNumber — 实时跳动数字组件
 * 模拟收益持续增长的视觉效果：每隔一段随机时间，数字微增并带有翻转动画
 */
interface TickingNumberProps {
  /** 基础数值（字符串，如 "12.45"） */
  baseValue: string;
  /** 前缀，如 "+" */
  prefix?: string;
  /** 后缀，如 " U" */
  suffix?: string;
  /** 最小跳动间隔 ms */
  minInterval?: number;
  /** 最大跳动间隔 ms */
  maxInterval?: number;
  /** 每次增量范围 [min, max] */
  incrementRange?: [number, number];
  /** 额外 className */
  className?: string;
  /** 额外 style */
  style?: React.CSSProperties;
}

export default function TickingNumber({
  baseValue,
  prefix = "",
  suffix = "",
  minInterval = 2000,
  maxInterval = 5000,
  incrementRange = [0.01, 0.08],
  className = "",
  style,
}: TickingNumberProps) {
  const base: number = parseFloat(baseValue) || 0;
  const decimals = baseValue.includes(".") ? baseValue.split(".")[1].length : 2;
  const [current, setCurrent] = useState(base);
  const [direction, setDirection] = useState<"up" | "down">("up");
  const prevRef = useRef(base);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const tick = () => {
      const [minInc, maxInc] = incrementRange;
      const inc = minInc + Math.random() * (maxInc - minInc);
      setCurrent(prev => {
        const next = +(prev + inc).toFixed(decimals);
        setDirection(next >= prevRef.current ? "up" : "down");
        prevRef.current = next;
        return next;
      });
      const delay = minInterval + Math.random() * (maxInterval - minInterval);
      timerRef.current = setTimeout(tick, delay);
    };
    const initialDelay = 800 + Math.random() * 2000;
    timerRef.current = setTimeout(tick, initialDelay);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [minInterval, maxInterval, incrementRange, decimals]);

  const formatted = current.toFixed(decimals);
  const digits = (prefix + formatted + suffix).split("");

  return (
    <span className={`inline-flex items-baseline ${className}`} style={style}>
      <AnimatePresence mode="popLayout">
        {digits.map((char, i) => {
          const isDigit = /\d/.test(char);
          if (!isDigit) {
            return (
              <span key={`static-${i}-${char}`} className="inline-block">
                {char}
              </span>
            );
          }
          return (
            <motion.span
              key={`d-${i}-${char}-${formatted}`}
              initial={{ y: direction === "up" ? 6 : -6, opacity: 0.3 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: direction === "up" ? -6 : 6, opacity: 0 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="inline-block"
            >
              {char}
            </motion.span>
          );
        })}
      </AnimatePresence>
    </span>
  );
}
